/**
 * Shares the slide's playback state with every object renderer.
 *
 * Deliberately a context rather than a wrapper component: slide objects are absolutely
 * positioned against `.slide-container`, and wrapping them in an animated div would make
 * that div a containing block whenever a transform is active (Fly In, Zoom), shifting the
 * object. Each renderer instead merges the returned style into its own root element.
 */

import { createContext, useContext } from 'react';

/** Default: nothing animates, everything visible. Used outside a provider (thumbnails). */
const NO_ANIMATION = {
  stateFor: () => ({ state: 'shown', style: {} }),
  advance: () => false,
  stepBack: () => false,
  rewind: () => {},
  playAll: () => {},
  stepIndex: -1,
  groupCount: 0,
  groupDurations: [],
  isFinished: true,
};

export const AnimationContext = createContext(NO_ANIMATION);

export const useAnimationContext = () => useContext(AnimationContext);

/**
 * Playback state for one object, or one SmartArt node within it.
 * Returns `{ state: 'hidden'|'playing'|'shown', style }` — spread `style` onto the root.
 */
export const useObjectAnimation = (targetId, subTargetId = null) => {
  const { stateFor } = useAnimationContext();
  return stateFor(targetId, subTargetId);
};

export default AnimationContext;
